/**
 * AssessmentPdfRegenCard
 * One-shot admin tool — fixes severity labels on client-portal-submitted
 * assessments mis-scored by the old severity-band bug, and regenerates
 * their chart PDFs (correct severity + fixed MRN display).
 * Dry-run scan first, then apply. Idempotent — safe to re-run.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileCheck2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AssessmentPdfRegenCard = () => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (dryRun) setLoading(true); else setApplying(true);
    try {
      const resp = await axios.post(`${API}/admin/tools/fix-portal-assessments`, { dry_run: dryRun });
      setResult(resp.data);
      if (dryRun) {
        toast.success(resp.data.matched > 0
          ? `Found ${resp.data.matched} assessment${resp.data.matched === 1 ? '' : 's'} to fix`
          : 'Nothing to fix — all portal assessments are correct');
      } else {
        toast.success(`Fixed ${resp.data.fixed_severity} severit${resp.data.fixed_severity === 1 ? 'y' : 'ies'}, regenerated ${resp.data.regenerated_pdfs} PDF${resp.data.regenerated_pdfs === 1 ? '' : 's'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fix failed');
    } finally {
      setLoading(false);
      setApplying(false);
    }
  };

  const matches = result?.matches || [];
  const canApply = result?.dry_run && result.matched > 0;

  return (
    <Card className="border-teal-200" data-testid="assessment-pdf-regen-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileCheck2 className="h-5 w-5 text-teal-600" />
          Portal Assessment Severity &amp; PDF Fix
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600">
          Recomputes severity on client-portal-submitted assessments mis-labeled
          by the old severity-band bug, then regenerates their chart PDFs with
          the corrected severity and fixed MRN display. The stale PDF is replaced
          in the client chart. Safe to re-run. Dry-run by default.
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => run(true)}
            disabled={loading || applying}
            data-testid="assessment-regen-scan-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            {loading ? 'Scanning…' : 'Scan (dry-run)'}
          </Button>
          <Button
            size="sm"
            variant="default"
            className="bg-teal-600 hover:bg-teal-700 text-white"
            disabled={!canApply || applying}
            onClick={() => {
              if (!window.confirm(
                `Fix ${result.matched} assessment${result.matched === 1 ? '' : 's'}? ` +
                'Severity labels will be corrected and the chart PDFs replaced with regenerated copies.'
              )) return;
              run(false);
            }}
            data-testid="assessment-regen-apply-btn"
          >
            {applying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            {applying ? 'Applying…' : `Fix & Regenerate${result?.dry_run && result?.matched ? ` (${result.matched})` : ''}`}
          </Button>
        </div>

        {result && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-1" data-testid="assessment-regen-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={result.dry_run ? 'border-slate-400 text-slate-700' : 'border-green-400 text-green-800 bg-green-50'}>
                {result.dry_run ? 'Dry-run' : 'Applied'}
              </Badge>
              <span>Matched: <strong>{result.matched}</strong></span>
              {!result.dry_run && (
                <>
                  <span>Severities fixed: <strong>{result.fixed_severity}</strong></span>
                  <span>PDFs regenerated: <strong>{result.regenerated_pdfs}</strong></span>
                  {result.errors?.length > 0 && (
                    <span className="text-red-600">Errors: <strong>{result.errors.length}</strong></span>
                  )}
                </>
              )}
            </div>
            {matches.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-slate-700 hover:text-slate-900">
                  View assessments ({matches.length})
                </summary>
                <div className="mt-2 max-h-60 overflow-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-slate-600 text-left">
                        <th className="px-1 py-0.5 font-semibold">Assessment</th>
                        <th className="px-1 py-0.5 font-semibold">Date</th>
                        <th className="px-1 py-0.5 font-semibold">Score</th>
                        <th className="px-1 py-0.5 font-semibold">Severity</th>
                        <th className="px-1 py-0.5 font-semibold">Needs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {matches.map((m) => (
                        <tr key={m.score_id} className="hover:bg-slate-100">
                          <td className="px-1 py-0.5">{m.template_name || '—'}</td>
                          <td className="px-1 py-0.5">{m.assessment_date || '—'}</td>
                          <td className="px-1 py-0.5">{m.total_score ?? '—'}</td>
                          <td className="px-1 py-0.5">
                            {m.severity_wrong
                              ? <span>{m.old_severity || '—'} → <strong className="text-green-700">{m.new_severity}</strong></span>
                              : (m.new_severity || '—')}
                          </td>
                          <td className="px-1 py-0.5">
                            {m.severity_wrong ? 'severity + PDF' : 'PDF only'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssessmentPdfRegenCard;
